import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Markdown from 'react-native-markdown-display';
import { AppDataMediaItem } from "@/data/app-data-interfaces";
import { useCurrentTheme } from "@/theming/useCurrentTheme";
import { ThemeType } from "@/theming/baseTheme";

interface VideoDescriptionProps {
    video: AppDataMediaItem;
}

export const VideoDescription: React.FC<VideoDescriptionProps> = ({ video }) => {
    const { theme } = useCurrentTheme();
    const styles = createStyles(theme);

    // Check if there's any description content to show
    const hasDescription = video.shortDescription?.trim() || video.description?.trim();

    if (!hasDescription) {
        return null;
    }

    return (
        <View style={styles.container}>
            <Text style={styles.title}>About this video</Text>
            {video.description?.trim() && (
                <Markdown style={markdownStyles(theme)}>{video.description}</Markdown>
            )}
        </View>
    );
};

// Markdown styles function
const markdownStyles = (theme: ThemeType) => ({
    body: {
        color: theme.colors.textSecondary,
        fontSize: 14,
        lineHeight: 20,
    },
    heading1: {
        color: theme.colors.text,
        fontSize: 18,
        fontWeight: '700' as const,
        marginBottom: 8,
        marginTop: 16,
    },
    heading2: {
        color: theme.colors.text,
        fontSize: 16,
        fontWeight: '600' as const,
        marginBottom: 6,
        marginTop: 12,
    },
    heading3: {
        color: theme.colors.text,
        fontSize: 14,
        fontWeight: '600' as const,
        marginBottom: 4,
        marginTop: 8,
    },
    paragraph: {
        color: theme.colors.textSecondary,
        fontSize: 14,
        lineHeight: 20,
        marginBottom: 8,
    },
    strong: {
        color: theme.colors.text,
        fontWeight: '600' as const,
    },
    em: {
        color: theme.colors.text,
        fontStyle: 'italic' as const,
    },
    link: {
        color: theme.colors.primary,
        textDecorationLine: 'underline' as const,
    },
    list_item: {
        color: theme.colors.textSecondary,
        fontSize: 14,
        lineHeight: 20,
        marginBottom: 4,
    },
    bullet_list: {
        marginBottom: 8,
    },
    ordered_list: {
        marginBottom: 8,
    },
    code_inline: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: theme.colors.primary,
        fontFamily: 'monospace',
        fontSize: 13,
        paddingHorizontal: 4,
        paddingVertical: 2,
        borderRadius: 3,
    },
    fence: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 6,
        padding: 12,
        marginVertical: 8,
    },
    code_block: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        color: theme.colors.text,
        fontFamily: 'monospace',
        fontSize: 13,
        lineHeight: 18,
    },
    blockquote: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderLeftWidth: 3,
        borderLeftColor: theme.colors.primary,
        paddingLeft: 12,
        paddingVertical: 8,
        marginVertical: 8,
    },
    hr: {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        height: 1,
        marginVertical: 16,
    },
});

const createStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 12,
        padding: 16,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        marginBottom: 20
    },
    title: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: 8,
    },
});
